import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Star, Crown, Zap, Users, Shield, Clock, Award, ArrowRight, CheckCircle, XCircle, TrendingUp, BookOpen, Target, BarChart3 } from "lucide-react";
import { Button } from "../../ui/button";
import Header from "../../mainpagecomponents/Header";
import Footer from "../../mainpagecomponents/Footer";
import pricingPlans from "./hooks/pricingPlans";
import TeacherLogin from "../../components/LoginModules/TeacherLogin";
import { Dialog } from "@mui/material";




//Pricing Modules



const plans = pricingPlans;

const PricingPage = () => {
  const [loginOpen, setLoginOpen] = useState(false);
  return (
    <div className="min-h-screen bg-cream">
      <Header />
      <main>
        {/* Hero + Pricing Cards Section */}
        <section className="relative bg-gradient-to-b from-primary to-white pt-32 pb-20 px-2 sm:px-0">
          <div className="container mx-auto max-w-5xl text-center mb-12">
            <motion.h1
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary mb-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Empowering Teachers To Succeed With Smart Tools
            </motion.h1>
            <motion.p
              className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-10"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              Choose the plan that fits your needs. All plans include core Gradely features and support.<br />

              <span className="font-bold text-white">Start Your Free Trial Today!{" "} No Credit Card Needed!</span>
            </motion.p>
          </div>
          <div className="container mx-auto max-w-5xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[0, 1, 2].map((idx) => {
                const plan = plans[idx];
                return (
                  <motion.div
                    key={plan.name}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: idx * 0.1 }}
                    className={`relative flex flex-col rounded-3xl shadow-lg border bg-white px-8 py-12 items-center text-center transition-all duration-300
                      ${plan.highlight ? "ring-2 ring-primary border-primary z-10 scale-105 bg-[#f7f7f2] shadow-2xl" : "border-gray-200"}
                    `}
                  >
                    {plan.highlight && (
                      <span className="absolute -top-5 left-1/2 -translate-x-1/2 bg-primary text-white px-5 py-1.5 rounded-full text-xs font-semibold shadow">
                        Most Popular
                      </span>
                    )}
                    <div className="flex flex-col items-center mb-7">
                      <div className="mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
                        <plan.icon className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="text-2xl font-bold text-primary mb-1">{plan.name}</h3>
                      <p className="text-foreground/60 text-base mb-2 text-center">{plan.description}</p>
                    </div>
                    <div className="mb-7 flex flex-col items-center">
                      <span className="text-5xl font-extrabold text-primary leading-tight">
                        {plan.price}
                      </span>
                      <span className="text-lg font-medium text-foreground/60 mt-1">
                        {plan.period}
                      </span>
                    </div>
                    <ul className="mb-10 space-y-3 w-full text-left">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-foreground/80 text-base">
                          <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button

                      onClick={() => setLoginOpen(true)}
                      className={`w-full py-3 px-4 text-lg font-semibold mt-auto rounded-xl shadow-sm transition-all duration-200
                        ${plan.highlight ? "bg-primary hover:bg-primary/90 text-white" : "bg-secondary hover:bg-secondary/80 text-primary"}
                      `}
                    >
                      {plan.cta}
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Feature Comparison Section */}
        <section className="py-16 sm:py-20 lg:py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-4">
                Compare Plans
              </h2>
              <p className="text-lg sm:text-xl text-foreground/60 max-w-3xl mx-auto">
                See exactly what each plan offers to make the best choice for your educational needs.
              </p>
            </motion.div>

            {/* Responsive Table (desktop only) */}
            <div className="hidden lg:block overflow-x-auto w-full">
              <table className="min-w-[600px] w-full border-separate border-spacing-y-4">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-white rounded-l-xl px-4 py-4 text-base font-semibold text-primary text-left shadow-sm">Features</th>
                    <th className="px-4 py-4 text-base font-semibold text-primary text-center bg-white">
                      Starter
                    </th>
                    <th className="px-4 py-4 text-base font-semibold text-primary text-center bg-primary/20 text-primary rounded-t-xl shadow-sm rounded-r-xl">
                      Premium (Monthly/Annual)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Map features for each row */}
                  {[
                    { label: 'Assessments per month', values: ['15', 'Unlimited'] },
                    { label: 'DOK Analysis', values: ['Basic', 'Advanced with insights'] },
                    { label: 'AI Comment Generation', values: [false, true] },
                    { label: 'Resource Generators', values: ['Limited', 'Unlimited (Unit Plans, Lesson Plans, Rubrics, etc.)'] },
                    { label: 'Support Response', values: ['48 hours', '24 hours'] },
                    { label: 'Progress Tracking', values: [false, true] },
                    { label: 'Student Dashboard', values: [false, true] },
                    { label: 'Analytics & Reports', values: [false, true] },
                  ].map((row, i) => (
                    <tr key={i} className="">
                      <td className="sticky left-0 z-10 bg-white rounded-l-xl px-4 py-4 text-foreground/90 text-base font-medium shadow-sm min-w-[160px] align-middle">{row.label}</td>
                      {row.values.map((val, idx) => (
                        <td
                          key={idx}
                          className={`px-4 py-4 text-center align-middle text-base font-medium ${idx === 1 ? 'bg-primary/10' : 'bg-white'} ${idx === 1 ? 'rounded-r-xl' : ''}`}
                        >
                          {typeof val === 'boolean' ? (
                            val ? <CheckCircle className="w-5 h-5 mx-auto text-green-500" /> : <XCircle className="w-5 h-5 mx-auto text-red-400" />
                          ) : (
                            <span className="block mx-auto text-foreground/80">{val}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Cards for mobile and tablet (up to lg) */}
            <div className="block lg:hidden mt-10">
              {[
                {
                  name: 'Starter',
                  icon: Zap,
                  highlight: false,
                  features: [
                    { label: 'Assessments per month', value: '15' },
                    { label: 'DOK Analysis', value: 'Basic' },
                    { label: 'AI Comment Generation', value: false },
                    { label: 'Resource Generators', value: 'Limited' },
                    { label: 'Support Response', value: '48 hours' },
                    { label: 'Progress Tracking', value: false },
                    { label: 'Student Dashboard', value: false },
                    { label: 'Analytics & Reports', value: false },
                  ]
                },
                {
                  name: 'Premium (Monthly/Annual)',
                  icon: Star,
                  highlight: true,
                  features: [
                    { label: 'Assessments per month', value: 'Unlimited' },
                    { label: 'DOK Analysis', value: 'Advanced with insights' },
                    { label: 'AI Comment Generation', value: true },
                    { label: 'Resource Generators', value: 'Unlimited (Unit Plans, Lesson Plans, Rubrics, etc.)' },
                    { label: 'Support Response', value: '24 hours' },
                    { label: 'Progress Tracking', value: true },
                    { label: 'Student Dashboard', value: true },
                    { label: 'Analytics & Reports', value: true },
                  ]
                }
              ].map((plan, idx) => (
                <div key={plan.name} className={`mb-8 rounded-2xl shadow border ${plan.highlight ? 'border-primary bg-primary/5' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-center gap-3 px-6 pt-6 pb-2">
                    <plan.icon className="w-6 h-6 text-primary" />
                    <span className="text-lg font-bold text-primary">{plan.name}</span>
                  </div>
                  <ul className="divide-y divide-gray-100">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center justify-between px-6 py-3 text-base">
                        <span className="text-foreground/80">{feature.label}</span>
                        {typeof feature.value === 'boolean' ? (
                          feature.value ? <CheckCircle className="w-5 h-5 text-green-500" /> : <XCircle className="w-5 h-5 text-red-400" />
                        ) : (
                          <span className="text-foreground/90 font-medium">{feature.value}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>




        {/* Value Proposition Section */}
        <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-primary/5 to-secondary/10">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-4">
                Why Choose Gradely?
              </h2>
              <p className="text-lg sm:text-xl text-foreground/60 max-w-3xl mx-auto">
                Join thousands of educators who have transformed their assessment practice with our AI-powered platform.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
              {[
                {
                  icon: TrendingUp,
                  title: "Proven Results",
                  description: "Teachers report 40% improvement in student engagement and 60% reduction in assessment time."
                },
                {
                  icon: Target,
                  title: "DOK-Aligned",
                  description: "Built specifically for Depth of Knowledge assessment with standards-aligned feedback."
                },
                {
                  icon: BookOpen,
                  title: "Easy Integration",
                  description: "Seamlessly integrates with your existing LMS and classroom workflow."
                },
                {
                  icon: BarChart3,
                  title: "Data-Driven Insights",
                  description: "Get actionable insights into student progress and learning outcomes."
                },
                {
                  icon: Shield,
                  title: "Secure & Compliant",
                  description: "FERPA-compliant with enterprise-grade security to protect student data."
                },
                {
                  icon: Users,
                  title: "Expert Support",
                  description: "Dedicated support team with education expertise to help you succeed."
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 sm:p-8 shadow-lg"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 mb-4 sm:mb-6">
                    <item.icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-primary mb-3 sm:mb-4">{item.title}</h3>
                  <p className="text-sm sm:text-base text-foreground/70 leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 sm:py-20 lg:py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg sm:text-xl text-foreground/60 max-w-3xl mx-auto">
                Everything you need to know about our pricing, plans, and getting started.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
              {[
                {
                  question: "Can I switch plans at any time?",
                  answer: "Yes, you can upgrade or downgrade your plan at any time. Changes will be prorated and reflected in your next billing cycle. Downgrades take effect at the end of your current billing period."
                },
                {
                  question: "Is there a free trial?",
                  answer: "Yes! Our Starter plan is completely free forever with no credit card required. You can upgrade to a paid plan whenever you're ready to unlock additional features and higher usage limits."
                },
                {
                  question: "What payment methods do you accept?",
                  answer: "We accept all major credit cards (Visa, MasterCard, American Express). All payments are processed securely through Stripe."
                },
                {
                  question: "Do you offer refunds?",
                  answer: "Refunds are not automatically processed. However, if you have canceled your subscription, you can email us at support@gradely.ca or use the Help button within the app. Our team will review your request and do our best to issue you a refund!"
                },
                {
                  question: "Can I cancel my subscription?",
                  answer: "Yes. You can cancel your subscription by logging in, navigating to Subscription > Manage Subscription, and following the prompts. You'll continue to have access to all features until the end of your billing period."
                },
                {
                  question: "Do you offer educational discounts?",
                  answer: "Yes, we offer special pricing for schools, districts, and educational institutions. Contact our sales team at sales@gradely.ca for volume discounts and custom pricing options."
                },
                {
                  question: "Is my data secure?",
                  answer: "Absolutely. We use enterprise-grade security with encryption at rest and in transit. We're FERPA-compliant and never share your data with third parties."
                },
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-cream rounded-xl p-6 sm:p-8"
                >
                  <h3 className="text-lg sm:text-xl font-semibold text-primary mb-3 sm:mb-4">{faq.question}</h3>
                  <p className="text-sm sm:text-base text-foreground/70 leading-relaxed">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-20 lg:py-24 bg-primary">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Ready to Transform Your Assessment Practice?
              </h2>
              <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-3xl mx-auto leading-relaxed">
                Join thousands of educators who are already using Gradely to provide meaningful feedback and improve student outcomes. Start your free trial today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="bg-white text-primary hover:bg-white/90 px-8 py-3 text-lg font-semibold" onClick={() => setLoginOpen(true)}>
                  Start Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
              <p className="text-white/60 text-sm sm:text-base mt-6">
                No credit card required • 30-day free trial • Cancel anytime
              </p>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <Dialog open={loginOpen} onClose={() => setLoginOpen(false)} maxWidth="sm" fullWidth>
        <TeacherLogin onClose={() => setLoginOpen(false)} />
      </Dialog>
    </div>
  );
};

export default PricingPage; 